import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from './../../services/api';
import { IScheduleCredentials, useAuth } from './../../hooks/auth';
import { saveScheduleRequest, hasScheduleRequest, getScheduleRequest, clearScheduleRequest } from './../../utils/scheduleUtils';
import LoadingComponent from './../../components/Loading/';
import Select from 'react-select';
import { useHistory, useLocation } from 'react-router';
import { FormContainer, CheckBoxContainer } from './styles';
import CustomSelect from '../Select'
import Cookies from 'js-cookie';
import { getHttpStatus } from '../../utils/authRedirect';
import {
	DAILY_VISIT_LIMIT_MESSAGE,
	filterScheduleSlotsByDailyVisitLimit,
	getScheduleApiErrorMessage,
} from '../../utils/dailyVisitLimit';
import { pushScheduleVisit } from '../../utils/dataLayer';

interface IState {
	consultant_id: number;
	property_id: number;
	title?: string | null;
	rent_value?: string | null;
	city?: string | null;
	uf?: string | null;
	neighborhood?: string | null;
}

function listingFromState(routeState: IState | undefined) {
	return {
		title: routeState?.title ?? null,
		rentValue: routeState?.rent_value ?? null,
		city: routeState?.city ?? null,
		uf: routeState?.uf ?? null,
		neighborhood: routeState?.neighborhood ?? null,
	};
}

interface IDates {
	id: number,
	date: string;
	consultant_id: number;
	status: number;
	booked_count?: number;
	max_capacity?: number;
}

interface IUserAppointment {
	id: number;
	date: string;
	property_id: number;
	property_title: string;
	property_address: string;
}

interface ISelectLib {
	value: string;
	label: string;
}

interface ISelectOption {
	value: string;
	label: string;
}

const CHANNEL_LIST: Array<ISelectLib> = [
	{ label: 'OLX', value: 'olx' },
	{ label: 'Facebook', value: 'facebook' },
	{ label: 'ZAP Imóveis', value: 'zap' },
	{ label: 'VivaReal', value: 'vivaReal' },
	{ label: 'Google', value: 'google' },
	{ label: 'Instagram', value: 'instagram' },
	{ label: 'Outro', value: 'outro' }
];

const UTM_KEYS = ["utm_source", "utm_campaign", "utm_medium", "utm_content"] as const;
type UtmKey = typeof UTM_KEYS[number];

function readUtmFromCookies(): Partial<Record<UtmKey, string>> {
  const utm: Partial<Record<UtmKey, string>> = {};
  UTM_KEYS.forEach((k) => {
    const v = Cookies.get(k);
    if (v) utm[k] = v;
  });
  return utm;
}

export default function FormToSchedulePage() {
	const history = useHistory();
	const { scheduleAppointment, user } = useAuth();
	const { state } = useLocation<IState | undefined>();
	const scheduleRequest = useMemo(() => getScheduleRequest(), []);

	const [dates, setDates] = useState<IDates[]>([]);
	const [hours, setHours] = useState<ISelectOption[]>([]);
	const [userAppointments, setUserAppointments] = useState<IUserAppointment[]>([]);
	const [loading, setLoading] = useState(true);

	const [selectedDate, setSelectedDate] = useState<string>("");
	const [selectedHour, setSelectedHour] = useState<string>("");
	const [videoCall, setVideoCall] = useState(0);

	const [selectedChannel, setSelectedChannel] = useState<string>(() => {
		const cookieValue = Cookies.get('campanha');
		return CHANNEL_LIST.find(ch => ch.value === cookieValue)?.value ?? '';
	});

	const [error, setError] = useState('');
	const [errorInSelectedDate, setErrorInSelectedDate] = useState(false);
	const [errorInSelectedHour, setErrorInSelectedHour] = useState(false);
	const [scheduleConflict, setScheduleConflict] = useState<{ hasConflict: boolean, conflictingAppointment?: IUserAppointment }>({ hasConflict: false });

	const [loadingSubmit, setLoadingSubmit] = useState(false);

	const consultantId = useMemo(
		() => state?.consultant_id ?? scheduleRequest?.state?.consultant_id,
		[state, scheduleRequest]
	);
	const propertyId = useMemo(
		() => state?.property_id ?? scheduleRequest?.state?.property_id ?? scheduleRequest?.propertyId,
		[state, scheduleRequest]
	);

	const hasAvailableSlots = useCallback((schedule: IDates): boolean => {
		if (schedule.booked_count !== undefined && schedule.max_capacity !== undefined) {
			return schedule.booked_count < schedule.max_capacity;
		}

		return schedule.status === 1 || schedule.status === 2;
	}, []);

	const checkScheduleConflict = useCallback((dateTime: string): { hasConflict: boolean, conflictingAppointment?: IUserAppointment } => {
		if (!user || userAppointments.length === 0) {
			return { hasConflict: false };
		}

		const selectedDateTime = new Date(dateTime);

		for (const appointment of userAppointments) {
			const appointmentDateTime = new Date(appointment.date);

			if (
				selectedDateTime.getFullYear() === appointmentDateTime.getFullYear() &&
				selectedDateTime.getMonth() === appointmentDateTime.getMonth() &&
				selectedDateTime.getDate() === appointmentDateTime.getDate() &&
				selectedDateTime.getHours() === appointmentDateTime.getHours()
			) {
				return {
					hasConflict: true,
					conflictingAppointment: appointment
				};
			}
		}

		return { hasConflict: false };
	}, [user, userAppointments]);

	useEffect(() => {
		if (!state && !scheduleRequest?.state) {
			history.push('/');
			return;
		}

		loadingData();
	}, [consultantId, propertyId, user, history, state, scheduleRequest]);

	const loadingData = async () => {
		try {
			if (!state && !scheduleRequest?.state) {
				setLoading(false);
				history.push('/');
				return;
			}

			let currentConsultantId = consultantId;
			let currentPropertyId = propertyId;

			if (!currentConsultantId && currentPropertyId) {
				const response = await api.get(`/api/client/properties/${currentPropertyId}`);
				currentConsultantId = response.data.data.consultant_id;
			}

			if (!currentConsultantId) {
				throw new Error('Consultant ID not found');
			}

			if (!currentPropertyId) {
				throw new Error('Property ID not found');
			}

			const response = await api.get(
				`/api/client/schedules/consultant/${currentConsultantId}/property/${currentPropertyId}`
			);
			setDates(response.data.data);

			if (user) {
				try {
					const appointmentsResponse = await api.get('/api/client/schedules/user/appointments');
					setUserAppointments(appointmentsResponse.data.data || []);
				} catch (error) {
					console.warn('Não foi possível carregar os agendamentos do usuário:', error);
				}
			}

			if (hasScheduleRequest()) {
				try {
					setLoading(true);
					const scheduleRequest = getScheduleRequest();
					const response = await scheduleAppointment(scheduleRequest.data);
					if (response.success) {
						pushScheduleVisit({
							propertyId: scheduleRequest.data.property_id,
							scheduleId: scheduleRequest.data.id,
							visitDate: scheduleRequest.fields?.selectedDate ?? "",
							visitTime: scheduleRequest.fields?.selectedHour ?? "",
							videoCall: scheduleRequest.data.video_call ?? null,
							originSource: scheduleRequest.data.origin_source ?? null,
							utmSource: scheduleRequest.data.utm_source ?? null,
							utmCampaign: scheduleRequest.data.utm_campaign ?? null,
							utmMedium: scheduleRequest.data.utm_medium ?? null,
							utmContent: scheduleRequest.data.utm_content ?? null,
							...listingFromState(scheduleRequest.state),
							user: {
								id: user?.id,
								first_name: user?.first_name,
								email: user?.email,
								phone: user?.phone,
							},
						});
						setLoading(false);
						clearScheduleRequest();
						history.push(`/schedule/${propertyId}/success`);
					}
				} catch (e) {
					if (getHttpStatus(e) === 422) {
						clearScheduleRequest();
					}
					setError(
						getScheduleApiErrorMessage(
							e,
							"Ocorreu um erro no agendamento. Por favor, tente novamente!"
						)
					);
				}
			}
			setLoading(false);

		} catch (e: any) {
			console.error(e);
			setLoading(false);
			const errorMessage = e.response?.data?.message || 'Erro ao carregar dados. Tente novamente.';
			setError(errorMessage);

			if (propertyId) {
				history.push(`/imovel/${propertyId}`);
			} else {
				history.push('/');
			}
		}
	}

	const datesWithinDailyLimit = useMemo(() => {
		const availableDates = dates.filter(date => hasAvailableSlots(date));
		const numericPropertyId = Number(propertyId);

		if (!user || !Number.isFinite(numericPropertyId)) {
			return availableDates;
		}

		return filterScheduleSlotsByDailyVisitLimit(
			availableDates,
			userAppointments,
			numericPropertyId
		);
	}, [dates, hasAvailableSlots, propertyId, user, userAppointments]);

	const dailyLimitBlockedAllDates =
		!!user &&
		dates.some((date) => hasAvailableSlots(date)) &&
		datesWithinDailyLimit.length === 0;

	const DatesFilter = useMemo(() => {
		const getDates = datesWithinDailyLimit.map(date => date.date.substring(0, 10));
		const uniques = [...new Set(getDates)];
		const orderDates = uniques.sort((a, b) => (+new Date(a) - +new Date(b)));

		const formatDate = orderDates.map((date, index) => {
			return {
				value: orderDates[index],
				label: `${date.substring(8, 10)}/${date.substring(5, 7)}/${date.substring(0, 4)}`
			};
		});

		return formatDate;
	}, [datesWithinDailyLimit]);

	const handleSubmit = useCallback(async () => {
		setLoadingSubmit(true);
		setError('');
		setScheduleConflict({ hasConflict: false });

		try {
			const effectiveState = state ?? scheduleRequest?.state;

			if (!effectiveState) {
				setError('Dados inválidos. Por favor, tente novamente.');
				setLoadingSubmit(false);
				return;
			}


			if (selectedDate === "") {
				setLoadingSubmit(false);
				setErrorInSelectedDate(true);
				return;
			}

			if (selectedHour === "") {
				setLoadingSubmit(false);
				setErrorInSelectedHour(true);
				return;
			}

			const selectedDateTime = `${selectedDate} ${selectedHour}`;

			const conflictCheck = checkScheduleConflict(selectedDateTime);
			if (conflictCheck.hasConflict) {
				setScheduleConflict(conflictCheck);
				setError(`Você já tem um agendamento para este horário: ${conflictCheck.conflictingAppointment?.property_title} às ${new Date(conflictCheck.conflictingAppointment!.date).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`);
				setLoadingSubmit(false);
				return;
			}

			const getID = dates.find(date => {
				const matchesDateTime = date.date === selectedDateTime;
				return matchesDateTime && hasAvailableSlots(date);
			});

			if (!!getID) {
				const utm = readUtmFromCookies();

				const scheduleRequest: IScheduleCredentials = {
					id: getID.id,
					video_call: String(videoCall),
					property_id: String(propertyId),

					userChannel: selectedChannel || undefined,

					origin_source: selectedChannel || null,

					utm_source: utm.utm_source ?? null,
					utm_campaign: utm.utm_campaign ?? null,
					utm_medium: utm.utm_medium ?? null,
					utm_content: utm.utm_content ?? null,
				};

				if (!!user) {
					const response = await scheduleAppointment(scheduleRequest);


					if (!response.success) {
						setLoadingSubmit(false);
						setError(response.message);
						return;
					}
					pushScheduleVisit({
						propertyId: scheduleRequest.property_id,
						scheduleId: scheduleRequest.id,
						visitDate: selectedDate,
						visitTime: selectedHour,
						videoCall: scheduleRequest.video_call ?? null,
						originSource: scheduleRequest.origin_source ?? null,
						utmSource: scheduleRequest.utm_source ?? null,
						utmCampaign: scheduleRequest.utm_campaign ?? null,
						utmMedium: scheduleRequest.utm_medium ?? null,
						utmContent: scheduleRequest.utm_content ?? null,
						...listingFromState(effectiveState),
						user: {
							id: user.id,
							first_name: user.first_name,
							email: user.email,
							phone: user.phone,
						},
					});
					clearScheduleRequest();
					history.push(`/schedule/${propertyId}/success`);
				} else {
					saveScheduleRequest({
						propertyId: propertyId!,
						state: effectiveState,
						data: scheduleRequest,
						fields: { selectedDate, selectedHour, selectedChannel, videoCall }
					});
					history.push(`/login`);
				}
			} else {
				setError('Horário não encontrado. Por favor, selecione novamente.');
			}

			setLoadingSubmit(false);
			setErrorInSelectedDate(false);
			setErrorInSelectedHour(false);
		} catch (err: any) {
			setLoadingSubmit(false);
			setError(
				getScheduleApiErrorMessage(
					err,
					'Erro ao processar agendamento. Verifique sua conexão e tente novamente.'
				)
			);
		}
	}, [selectedDate, selectedHour, dates, propertyId, videoCall, scheduleAppointment, selectedChannel, state, user, history, hasAvailableSlots, checkScheduleConflict]);

	const handleSelectDate = useCallback((selectedDate: string | undefined) => {
		if (selectedDate === "") {
			setSelectedDate("");
			setSelectedHour("");
			setHours([]);
			setScheduleConflict({ hasConflict: false });
			return;
		}

		if (!!selectedDate) {
		const getHours = datesWithinDailyLimit.filter(date => (
			date.date.includes(selectedDate) &&
			hasAvailableSlots(date)
		));

			const orderHours = getHours.sort((a, b) => {
				return Number(a.date.substring(11, 13)) - Number(b.date.substring(11, 13));
			});

			const formatHours = orderHours.map(hour => ({
				value: hour.date.substring(11, 19),
				label: hour.date.substring(11, 19)
			}));

			setSelectedDate(selectedDate);
			setHours(formatHours);
			setSelectedHour("");
			setScheduleConflict({ hasConflict: false });
		}
	}, [datesWithinDailyLimit, hasAvailableSlots]);

	const handleSelectHour = useCallback((hour: string | undefined) => {
		if (hour === "") {
			setSelectedHour("");
			setScheduleConflict({ hasConflict: false });
			return;
		}
		if (!!selectedDate && !!hour) {
			setSelectedHour(hour);

			const selectedDateTime = `${selectedDate} ${hour}`;
			const conflictCheck = checkScheduleConflict(selectedDateTime);
			setScheduleConflict(conflictCheck);
		}
	}, [selectedDate, checkScheduleConflict]);

	const handleSelectVideoCall = useCallback((option: number) => {
		if (videoCall === option) {
			return setVideoCall(0);
		}

		setVideoCall(option);
	}, [videoCall]);

	if (!state) {
		return <LoadingComponent />;
	}

	const customSelectStyles = {
		control: (base: any) => ({
			...base,
			minHeight: '48px',
			borderRadius: 0,
			border: 0,
			borderBottom: '1px solid #e0e0e0',
			boxShadow: 'none',
			'&:hover': {
				borderBottom: '1px solid #bdbdbd',
			}
		}),
		menuPortal: (base: any) => ({
			...base,
			zIndex: 9999
		}),
		menu: (base: any) => ({
			...base,
			zIndex: 9999
		})
	};

	return (
		<>
			{loading && (
				<LoadingComponent />
			)}

			{!loading && (
				<FormContainer className="agendamento-container">
					{scheduleConflict.hasConflict && (
						<div className="conflict-alert">
							<strong>Conflito de Agenda Detectado</strong>
							<p>
								Você já tem um agendamento para este mesmo horário:
								<br />
								<strong>{scheduleConflict.conflictingAppointment?.property_title}</strong>
								<br />
								{scheduleConflict.conflictingAppointment?.property_address}
								<br />
								<em>Horário: {scheduleConflict.conflictingAppointment ?
									new Date(scheduleConflict.conflictingAppointment.date).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
									: ''}</em>
							</p>
							<p>
								Por favor, escolha um horário diferente.
							</p>
						</div>
					)}

					<p>Data de preferência: {!!errorInSelectedDate ? <span className="error-text">Escolha uma data</span> : ""}</p>
					<Select<ISelectOption>
						className="custom-select"
						classNamePrefix="schedule-select"
						options={DatesFilter}
						onChange={(e: ISelectOption | null) => handleSelectDate(e?.value)}
						placeholder="Escolha uma data"
						noOptionsMessage={() => dailyLimitBlockedAllDates
							? DAILY_VISIT_LIMIT_MESSAGE
							: "Não temos datas disponíveis para agendamento"}
						maxMenuHeight={250}
						menuPortalTarget={document.body}
						menuPlacement="auto"
						menuPosition="fixed"
						styles={customSelectStyles}
					/>

					<p>Hora de preferência: {!!errorInSelectedHour ? <span className="error-text">Escolha uma hora</span> : ""}</p>
					<Select<ISelectOption>
						className="custom-select"
						classNamePrefix="schedule-select"
						options={hours}
						onChange={(e: ISelectOption | null) => handleSelectHour(e?.value)}
						placeholder="Escolha um horário"
						noOptionsMessage={() => "Não temos horários disponíveis nessa data."}
						maxMenuHeight={150}
						isDisabled={scheduleConflict.hasConflict}
						menuPortalTarget={document.body}
						menuPlacement="auto"
						menuPosition="fixed"
						styles={customSelectStyles}
					/>

					<p>Onde você encontrou nosso anúncio?</p>
					<CustomSelect
						placeholder='Escolha um canal'
						value={selectedChannel}
						onChange={(e: string | null) => setSelectedChannel(e ?? '')}
						channelList={CHANNEL_LIST}
					>
						{CHANNEL_LIST.map(channel => (
							<option key={channel.value} value={channel.value}>
								{channel.label}
							</option>
						))}
					</CustomSelect>

					<CheckBoxContainer onClick={() => handleSelectVideoCall(1)}>
						<input type="checkbox" checked={videoCall === 1} onChange={() => handleSelectVideoCall(1)} />
						<label>Fazer visita via videochamada</label>
					</CheckBoxContainer>

					{error && !scheduleConflict.hasConflict && (
						<div className="error-alert">
							{error}
						</div>
					)}

					{!loadingSubmit ? (
						<button
							type="button"
							className={`agendamento-button ${scheduleConflict.hasConflict ? 'disabled-button' : error ? 'error-button' : ''}`}
							onClick={handleSubmit}
							disabled={scheduleConflict.hasConflict}
						>
							{scheduleConflict.hasConflict ? 'HORÁRIO INDISPONÍVEL' : 'AGENDAR'}
						</button>
					) : (
						<div className="loading-state">
							Carregando...
						</div>
					)}
				</FormContainer>
			)}
		</>
	);
}